import { MDX } from "./mdx/tokens";
import { LineChart, Activity, Layers } from "lucide-react";
import { PhoneFrame } from "@/components/frames/DeviceFrames";

/**
 * LiveTerminalShowcase — sits directly beneath PwaFeatureGrid on the landing.
 *
 * One eyebrow-titled feature ("Live Terminal — Charts, Signals, and Multi-
 * Asset Coverage") that renders THREE colour mobile mockups. Each phone is a
 * hand-rendered SVG-in-CSS scene tuned for the Melian Dialogue palette so the
 * design stays platform-consistent while showing genuinely differentiated
 * trading surfaces the visitor can imagine holding in their hand.
 *
 *   1) Live Candlestick — SCOM (NSE) OHLC with EMA line + volume rail
 *   2) BeCo Index Gauge — α_B / σ_B split gauges + weekly discipline sparkline
 *   3) Multi-Asset Watchlist — stocks, forex, commodities, indices in one card
 */
export default function LiveTerminalShowcase({ isDark }) {
    const bg = isDark ? "bg-[#090D16]" : "bg-white";
    const text = isDark ? "text-[#F9FAFB]" : "text-[#090D16]";
    const dim = isDark ? "text-[#9CA3AF]" : "text-[#6B7280]";

    const cards = [
        { id: "candles",  icon: LineChart, title: "Live Price Chart",     copy: "Watch prices move in real time. Tap any bar to see the day's high, low, and closing price." },
        { id: "beco",     icon: Activity,  title: "Your Progress Score",  copy: "A simple score of how disciplined — and how risky — your trading is, updated after every trade." },
        { id: "coverage", icon: Layers,    title: "Watchlist",            copy: "Shares, currencies, raw materials, and market trends — all in one list." },
    ];

    return (
        <section
            className={`${bg} ${text} py-16 md:py-24 border-t ${isDark ? "border-[#1F2937]" : "border-[#E5E7EB]"}`}
            data-testid="live-terminal-showcase"
        >
            <div className="max-w-[1400px] mx-auto px-4 md:px-8">
                <div className="mb-10">
                    <div className="overline mb-3" style={{ color: MDX.blueLow }}>
                        See It In Action
                    </div>
                    <h2 className="font-heading text-h2">
                        Watch prices move, get simple tips, and trade — right from your phone.
                    </h2>
                    <p className={`font-body text-body-lg mt-2 max-w-2xl ${dim}`}>
                        Add it to your home screen like any app. Then check prices, see how you're
                        doing, and browse African markets — even without internet.
                    </p>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                    {cards.map((c) => (
                        <div key={c.id} className="flex flex-col items-center text-center" data-testid={`live-card-${c.id}`}>
                            <PhoneMock id={c.id} isDark={isDark} />
                            <div className="mt-5 flex items-center gap-2 justify-center">
                                <c.icon className="h-4 w-4" style={{ color: MDX.blueLow }} strokeWidth={1.75} />
                                <div className="font-heading font-bold text-h3">{c.title}</div>
                            </div>
                            <p className={`font-body text-body-base mt-2 max-w-xs ${dim}`}>{c.copy}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ── Shared frame wrapper ─────────────────────────── */
function PhoneMock({ id, isDark }) {
    const panelBg = isDark ? "#0F1524" : "#FFFFFF";
    const brd = isDark ? "#1F2937" : "#E5E7EB";
    const text = isDark ? "#F9FAFB" : "#090D16";
    const dim = isDark ? "#9CA3AF" : "#6B7280";
    return (
        <div className="md-mock-anchor">
            <PhoneFrame>
                <div className="flex flex-col w-full h-full" style={{ background: panelBg, margin: -14, padding: 0 }}>
                    {id === "candles"  && <CandlesMock text={text} dim={dim} brd={brd} isDark={isDark} />}
                    {id === "beco"     && <BeCoMock    text={text} dim={dim} brd={brd} isDark={isDark} />}
                    {id === "coverage" && <CoverageMock text={text} dim={dim} brd={brd} isDark={isDark} />}
                </div>
            </PhoneFrame>
        </div>
    );
}

/* ── 1) Live Candlestick mockup ───────────────────── */
function CandlesMock({ text, dim, brd, isDark }) {
    // Static (yet plausible) OHLC values for SCOM · NSE. Green closes above open.
    const bars = [
        { o: 60, c: 72, hi: 74, lo: 55, up: true  },
        { o: 72, c: 66, hi: 78, lo: 62, up: false },
        { o: 66, c: 82, hi: 84, lo: 63, up: true  },
        { o: 82, c: 76, hi: 88, lo: 74, up: false },
        { o: 76, c: 95, hi: 98, lo: 74, up: true  },
        { o: 95, c: 88, hi:100, lo: 86, up: false },
        { o: 88, c:104, hi:108, lo: 86, up: true  },
        { o:104, c:110, hi:118, lo:100, up: true  },
        { o:110, c: 96, hi:114, lo: 90, up: false },
        { o: 96, c:118, hi:122, lo: 92, up: true  },
    ];
    const yMax = 130;
    const chartH = 108;
    const barW = 14;
    const gap  = 4;
    const y = (v) => chartH - (v / yMax) * chartH;
    // EMA polyline (rough)
    const emaVals = [66, 70, 74, 78, 82, 86, 92, 100, 104, 108];
    const emaPts = emaVals.map((v, i) => `${i * (barW + gap) + barW / 2},${y(v)}`).join(" ");

    return (
        <div className="p-4 flex flex-col h-full">
            <div className="flex items-center justify-between mb-2">
                <div>
                    <div className="font-heading font-bold text-[13px]" style={{ color: text }}>SCOM · NSE</div>
                    <div className="font-mono text-[10px]" style={{ color: dim }}>Safaricom PLC · 5m</div>
                </div>
                <div className="text-right">
                    <div className="font-mono font-bold text-[15px]" style={{ color: text }}>KES 18.35</div>
                    <div className="font-mono text-[10px] font-bold" style={{ color: "#16A34A" }}>+1.85 (+11.2%)</div>
                </div>
            </div>
            {/* Chart */}
            <div className="rounded-md p-2 mb-2" style={{ border: `1px solid ${brd}`, background: isDark ? "#090D16" : "#F9FAFB" }}>
                <svg width="100%" viewBox={`0 0 ${bars.length * (barW + gap)} ${chartH + 10}`} preserveAspectRatio="none" style={{ display: "block" }}>
                    {/* gridlines */}
                    {[0.25, 0.5, 0.75].map((f, i) => (
                        <line key={i} x1={0} x2={bars.length * (barW + gap)} y1={chartH * f} y2={chartH * f} stroke={brd} strokeDasharray="2 2" />
                    ))}
                    {/* candles */}
                    {bars.map((b, i) => {
                        const x = i * (barW + gap);
                        const openY = y(b.o), closeY = y(b.c);
                        const bodyTop = Math.min(openY, closeY);
                        const bodyH = Math.max(1, Math.abs(openY - closeY));
                        const color = b.up ? "#16A34A" : "#DC2626";
                        return (
                            <g key={i}>
                                <line x1={x + barW / 2} x2={x + barW / 2} y1={y(b.hi)} y2={y(b.lo)} stroke={color} strokeWidth={1.25} />
                                <rect x={x} y={bodyTop} width={barW} height={bodyH} fill={color} rx={1} />
                            </g>
                        );
                    })}
                    {/* EMA */}
                    <polyline points={emaPts} fill="none" stroke={MDX.blueLowSolid} strokeWidth={1.5} strokeLinecap="round" />
                </svg>
            </div>
            {/* Volume rail + OHLC readout */}
            <div className="rounded-md p-2" style={{ border: `1px solid ${brd}`, background: isDark ? "#090D16" : "#F9FAFB" }}>
                <div className="font-mono text-[9px] mb-1" style={{ color: dim }}>Vol · 24h</div>
                <div className="flex items-end gap-[2px] h-5">
                    {[45, 60, 30, 78, 52, 88, 40, 96, 70, 82].map((h, i) => (
                        <div key={i} style={{ height: `${h}%`, width: 8, background: i === 9 ? MDX.blueLowSolid : (isDark ? "#374151" : "#CBD5E1"), borderRadius: 1 }} />
                    ))}
                </div>
            </div>
            <div className="mt-2 grid grid-cols-4 gap-1 text-[9px] font-mono" style={{ color: dim }}>
                <div>O 17.05</div><div>H 18.44</div><div>L 16.90</div><div style={{ color: "#16A34A" }}>C 18.35</div>
            </div>
        </div>
    );
}

/* ── 2) BeCo Index Live gauge mockup ──────────────── */
function BeCoMock({ text, dim, brd, isDark }) {
    // Two half-donut gauges for α (discipline · GREEN good) and σ (tilt · RED bad).
    const gauge = ({ pct, color, label, val, sub }) => {
        const R = 30, CX = 40, CY = 34;
        const start = Math.PI, end = 0;
        const angle = start + (end - start) * (pct / 100);
        const large = pct > 50 ? 1 : 0;
        const sweep = 1;
        const arc = `M ${CX + R * Math.cos(start)} ${CY + R * Math.sin(start)} A ${R} ${R} 0 ${large} ${sweep} ${CX + R * Math.cos(angle)} ${CY + R * Math.sin(angle)}`;
        const bgArc = `M ${CX + R * Math.cos(start)} ${CY + R * Math.sin(start)} A ${R} ${R} 0 1 1 ${CX + R * Math.cos(end)} ${CY + R * Math.sin(end)}`;
        return (
            <div className="rounded-md p-2 text-center" style={{ border: `1px solid ${brd}`, background: isDark ? "#090D16" : "#F9FAFB" }}>
                <svg width="100%" viewBox="0 0 80 40" style={{ display: "block" }}>
                    <path d={bgArc} stroke={isDark ? "#1F2937" : "#E5E7EB"} strokeWidth={5} fill="none" strokeLinecap="round" />
                    <path d={arc}   stroke={color} strokeWidth={5} fill="none" strokeLinecap="round" />
                </svg>
                <div className="font-mono font-bold text-[13px] leading-none mt-0.5" style={{ color: text }}>{val}</div>
                <div className="font-heading font-bold text-[8px] uppercase tracking-widest mt-1" style={{ color }}>{label}</div>
                <div className="font-body text-[9px]" style={{ color: dim }}>{sub}</div>
            </div>
        );
    };
    const spark = [3, 5, 4, 7, 6, 8, 7, 9].map((v, i) => `${i * 14},${20 - v * 1.8}`).join(" ");
    return (
        <div className="p-4 flex flex-col h-full">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <div className="font-heading font-bold text-[13px]" style={{ color: text }}>BeCo Index</div>
                    <div className="font-mono text-[10px]" style={{ color: dim }}>Week 34 &middot; Tue 14:20</div>
                </div>
                <span className="text-[9px] font-heading font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border" style={{ borderColor: "#16A34A", color: "#16A34A" }}>Live</span>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-2">
                {gauge({ pct: 78, color: "#16A34A", label: "Alpha α",  val: "0.78", sub: "Discipline" })}
                {gauge({ pct: 42, color: "#EA580C", label: "Sigma σ",  val: "0.42", sub: "Tilt · risk" })}
            </div>
            <div className="rounded-md p-2 flex-1" style={{ border: `1px solid ${brd}`, background: isDark ? "#090D16" : "#F9FAFB" }}>
                <div className="flex items-center justify-between mb-1">
                    <div className="font-heading text-[9px] font-bold uppercase tracking-widest" style={{ color: dim }}>7-day α trend</div>
                    <div className="font-mono text-[10px] font-bold" style={{ color: "#16A34A" }}>+18%</div>
                </div>
                <svg width="100%" viewBox="0 0 100 22" preserveAspectRatio="none" style={{ display: "block" }}>
                    <polyline points={spark} fill="none" stroke="#16A34A" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </div>
            <div className="mt-2 text-[9px] font-body" style={{ color: dim }}>
                Next check-in: stop-loss adherence at close.
            </div>
        </div>
    );
}

/* ── 3) Multi-Asset Watchlist mockup ──────────────── */
function CoverageMock({ text, dim, brd, isDark }) {
    const rows = [
        { sym: "SCOM",   name: "Safaricom",     px: "18.35", chg: "+11.2%", up: true,  tag: "NSE",   klass: "Equity" },
        { sym: "EQTY",   name: "Equity Group",  px: "44.60", chg:  "+2.4%", up: true,  tag: "NSE",   klass: "Equity" },
        { sym: "USD/KES",name: "Dollar Shilling",px: "128.94",chg:  "-0.3%", up: false, tag: "FX",    klass: "Forex"  },
        { sym: "EUR/KES",name: "Euro Shilling", px: "138.11",chg:  "+0.5%", up: true,  tag: "FX",    klass: "Forex"  },
        { sym: "XAU",    name: "Spot Gold",     px: "3,415.20", chg: "+0.9%", up: true, tag: "CMD",   klass: "Metal"  },
        { sym: "BRENT",  name: "Brent Crude",   px:  "66.15", chg:  "-1.7%", up: false, tag: "CMD",   klass: "Energy" },
        { sym: "NSE20",  name: "NSE 20 Index",  px: "2,481.4", chg: "+0.4%", up: true,  tag: "IDX",   klass: "Index"  },
    ];
    return (
        <div className="p-3 flex flex-col h-full">
            <div className="flex items-center justify-between mb-2">
                <div>
                    <div className="font-heading font-bold text-[13px]" style={{ color: text }}>Watchlist</div>
                    <div className="font-mono text-[10px]" style={{ color: dim }}>7 symbols · Live</div>
                </div>
                <div className="flex gap-1">
                    {["ALL", "EQ", "FX", "CMD"].map((t, i) => (
                        <span key={t} className="text-[8px] font-heading font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                              style={{ background: i === 0 ? MDX.blueLowSolid : "transparent", color: i === 0 ? "#fff" : dim, border: `1px solid ${i === 0 ? MDX.blueLowSolid : brd}` }}>
                            {t}
                        </span>
                    ))}
                </div>
            </div>
            <div className="rounded-md overflow-hidden" style={{ border: `1px solid ${brd}` }}>
                {rows.map((r, i) => (
                    <div key={r.sym} className="flex items-center justify-between px-2 py-1.5"
                         style={{ borderTop: i === 0 ? "none" : `1px solid ${brd}`, background: isDark ? "#090D16" : "#FFFFFF" }}>
                        <div className="flex items-center gap-2 min-w-0">
                            <span className="font-mono text-[7px] font-bold uppercase tracking-wider px-1 rounded"
                                  style={{ background: r.tag === "NSE" ? MDX.blueLowSolid : r.tag === "FX" ? "#2563EB" : r.tag === "CMD" ? "#EA580C" : "#7C3AED", color: "#fff" }}>
                                {r.tag}
                            </span>
                            <div className="min-w-0">
                                <div className="font-heading font-bold text-[11px] leading-tight" style={{ color: text }}>{r.sym}</div>
                                <div className="font-body text-[9px] leading-tight truncate" style={{ color: dim }}>{r.name}</div>
                            </div>
                        </div>
                        <div className="text-right shrink-0">
                            <div className="font-mono font-bold text-[11px] leading-tight" style={{ color: text }}>{r.px}</div>
                            <div className="font-mono text-[9px] font-bold leading-tight" style={{ color: r.up ? "#16A34A" : "#DC2626" }}>{r.chg}</div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
